import type { Component } from 'solid-js';
import { For } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  roleCode: 'guardian' | 'child';
  onBack: () => void;
};

const GUARDIAN_GUIDES = [
  {
    title: 'Set up a child',
    steps: [
      'Open Kids and choose Add child.',
      'Choose your relationship, set a sign-in password, and confirm whether your family uses pounds or dollars.',
      'Review the starting bucket split before handing the account to the child.',
    ],
  },
  {
    title: 'Chores and routines',
    steps: [
      'Use All children to give every child their own copy of a chore or routine.',
      'Use Any child for one shared chore that any child can choose.',
      'Use Routines for repeating checklists and choose how many weeks between repeats.',
      'Review submitted work in Approvals before rewards reach buckets.',
    ],
  },
  {
    title: 'Money, allowances, and interest',
    steps: [
      'Open a child to record money, manage allowances, or edit bucket splits.',
      'Interest can be a teaching example or can add each payment to the real bucket balance.',
      'Activity history shows friendly descriptions; private system details stay hidden.',
    ],
  },
  {
    title: 'Check in and approve',
    steps: [
      'Open Approvals to see money, chores, and routine work waiting for you.',
      'Approve when it is right, or send it back with a kind note about what to try next.',
      'Use History when you want to see what happened before.',
    ],
  },
  {
    title: 'Work together as guardians',
    steps: [
      'Every guardian can help with every child, including chores, routines, money, and approvals.',
      'Choose each guardian relationship when you add them so the family list stays clear.',
      'Use the family tools to add, restore, or remove accounts when needed.',
    ],
  },
  {
    title: 'Keep your family safe',
    steps: [
      'Use a password only you know and reset a child password when they need help.',
      'You can review sign-in warnings and change a password from the account picker.',
      'Use Delete my data only for your own guardian account; family deletion is a separate choice.',
    ],
  },
] as const;

const CHILD_GUIDES = [
  {
    title: 'Finish a chore',
    steps: [
      'Open Chores and choose a job.',
      'Mark it finished and send it to your parent.',
      'Your reward reaches your buckets after they approve it.',
    ],
  },
  {
    title: 'Use your money',
    steps: [
      'Open Money to see your buckets.',
      'Choose a bucket to spend or ask to move money.',
      'You can never spend more than that bucket holds.',
    ],
  },
  {
    title: 'Do your routine',
    steps: [
      'Open today’s task and tick each step as you finish it.',
      'Send it to your grown-up when you are done.',
      'If they send it back, read their note and have another go.',
    ],
  },
  {
    title: 'Save for a goal',
    steps: [
      'Open Goals to see how close you are to something you want.',
      'Keep saving in the right bucket to make your goal grow.',
      'Ask a grown-up if you want to change a goal or your money split.',
    ],
  },
] as const;

export const TutorialsScreen: Component<Props> = (props) => {
  const guides = () => (props.roleCode === 'guardian' ? GUARDIAN_GUIDES : CHILD_GUIDES);
  return (
    <ActionScreen eyebrow="How ChorePop works" title="Tutorials" onBack={props.onBack}>
      <p class="action-screen-intro">
        Short guides you can return to whenever you need a reminder.
      </p>
      <div class="tutorial-list" data-testid="tutorial-list">
        <For each={guides()}>
          {(guide, index) => (
            <section class="tutorial-card">
              <span class="tutorial-number" aria-hidden="true">
                {index() + 1}
              </span>
              <div>
                <h2>{guide.title}</h2>
                <ol>
                  <For each={guide.steps}>{(step) => <li>{step}</li>}</For>
                </ol>
              </div>
            </section>
          )}
        </For>
      </div>
    </ActionScreen>
  );
};
